import React from "react";
import PropTypes from "prop-types";

import SocialShare from "./SocialShare";

const decimalFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1
});

const ExplanationContent = props => (
  <section className="cf pv4 ph4 ph2-ns content-section">
    <div className="cf mw8 center">
      <h2 className="f2 lh-title bb b--white tc">More about churn</h2>

      <div className="fl w-100 w-50-ns ph1 pr4-ns">
        <h3>What is churn?</h3>

        <p className="lh-copy f6">
          Customer churn, also known as customer attrition in some industries,
          is the rate at which you are losing your customer base.
        </p>
        <p className="lh-copy f6">
          While every business loses customers for various reasons, the higher
          your rate of churn the harder you'll have to work to keep the same
          amount of money coming into your business. Fighting to keep churn low
          and keep more customers improves your bottom line and is the sign of a
          healthy business.
        </p>

        <h3 className="pt3">How do you calculate churn?</h3>

        <p className="lh-copy f6">
          There are a lot of ways that a business could calculate its churn
          rate, ranging from very simple to intensely accurate. But for our
          purposes on this page we are keeping it simple to let you get a handle
          on your churn rate right away.
        </p>
        <p className="lh-copy f6">
          The simple manner we're using here is to divide the number of
          customers lost during a given month against the number of customers
          present at the beginning of that month.
        </p>

        <div className="lh-copy tc mw5 center i">
          <div className="bb">Customers Lost during Month</div>
          <div>Customers at Beginning of Month</div>
        </div>

        <p className="lh-copy f6">
          Or in other words, from the numbers you entered above:
        </p>

        <div className="lh-copy tc mw5 center i">
          <div className="dt center">
            <div className="dtc v-mid">
              {decimalFormatter.format(props.churnRate)}% =&nbsp;
            </div>
            <div className="dtc">
              <div className="bb">{props.totalChurn}</div>
              <div>{props.totalCustomers}</div>
            </div>
          </div>
        </div>

        <p className="lh-copy f6">
          You can get even fancier by dividing the number lost by the{" "}
          <em>average</em> number of customers you have in a month, or in other
          words, (Customers at BOM + Customers at EOM) / 2. Either approach will
          you give you your monthly churn rate as a percentage of your total
          customer base.
        </p>
        <p className="lh-copy f6">
          Watch out, because churn adds up fast! A churn rate of 5% may seem
          low, but over the course of the year it means you are turning over or
          losing more than half (60%) of your entire customer base. You'd be out
          of business in less than two years without growth.
        </p>

        <h3>What tools can I use to improve customer retention?</h3>

        <p className="lh-copy f6">
          The best tools you have are your eyes and ears. The more you listen to
          your customers and read what they're telling you, the better. Nothing
          can replace these essential "tools."
        </p>

        <p className="lh-copy f6">
          To multiply the number of people your eyes and ears can hear from,
          we're a bit biased but{" "}
          <a
            href="https://www.o3powered.com/outreach/features/?ref=churncalculator"
            className="tu white pl-gold"
          >
            we think our texting tool is the perfect thing
          </a>
          . Automatically send out a super short, two-question text message to
          your customers after and interaction and connect with them as the
          feedback rolls in.
        </p>
      </div>

      <div className="fl w-100 w-50-ns ph1 pl4-ns">
        <h3>How can I reduce churn?</h3>

        <p className="lh-copy f6">
          Avoiding churn is all about making people happy. Happy customers don't
          leave, but unhappy customers will.
        </p>
        <p className="lh-copy f6">
          Some churn is the inevitable result of customers moving away, lack of
          need, etc., but most churn arises from problems. The better you can
          solve your customers' problems the happier they will be, and the less
          likely they will be to churn out.
        </p>
        <p className="lh-copy f6">
          This means staying in touch with your customers. Pre- and post-service
          check-ins, follow-ups, quick response to inquiries, and more all go
          into creating a positive experience for your customer.
        </p>
        <p className="lh-copy f6">
          The more opportunities you create to have a real touchpoint with your
          customer the more they will be able to communicate back to you about
          issues that you might not otherwise have uncovered.
        </p>
        <p className="lh-copy f6">
          Here are some questions to ask yourself as you consider how you can
          improve your business's churn rate:
        </p>
        <ul className="lh-copy f6">
          <li>
            How would I know if my customer had a good experience when they last
            interacted with me?
          </li>
          <li>
            How can I found out how particular employees are affecting my
            customers' experience?
          </li>
          <li>
            Am I making it easy enough for someone to let me know about a
            problem that's arisen?
          </li>
          <li>
            What are the most likely problems my customers might be having? How
            can I get ahead of those issues to make the experience even better
            for them?
          </li>
        </ul>

        <h3 className="pt3">
          The bottom line benefits of improving customer retention
        </h3>

        <p className="lh-copy f6">
          You already know that you want to keep as many customers as you can,
          but how about one more piece of motivation?
        </p>

        <p className="lh-copy f6">
          Just like a churn rate can chip away at your customer base, chipping
          away at your churn rate dramatically recovers revenue. Using the
          figures you entered above, if you can bring churn from{" "}
          {decimalFormatter.format(props.churnRate)}% down by{" "}
          {props.improvement}% to a new rate of just{" "}
          {decimalFormatter.format(
            props.churnRate * (1 - props.improvement / 100.0)
          )}
          %, you will gain back {props.yearlyCumulative} dollars over the course
          of a year.
        </p>
        <p className="lh-copy f6">
          Little efforts each month make achievements like this possible. What
          could your business do with {props.yearlyCumulative} more money in the
          bank? What's the fastest way you can start implementing changes to
          bring about that improvement?
        </p>
        <p className="lh-copy f6">
          At O3 we believe that all truly good business is one-on-one, person to
          person. The more in touch you can be with your customers, the more
          opportunities you will have to serve them, and the happier they will
          be.
        </p>
      </div>
    </div>

    <div className="cf mw7 center tc mt3" style={{ fontSize: "12px" }}>
      <SocialShare />
    </div>
  </section>
);

ExplanationContent.propTypes = {
  totalCustomers: PropTypes.number.isRequired,
  totalChurn: PropTypes.number.isRequired,
  churnRate: PropTypes.number.isRequired,
  improvement: PropTypes.number.isRequired,
  yearlyCumulative: PropTypes.string.isRequired
};

export default ExplanationContent;



// WEBPACK FOOTER //
// ./src/ExplanationContent.js